/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Alert, Avatar, CircularProgress,
  InputAdornment, IconButton, Fade, Stack, Grid, useMediaQuery, useTheme, Checkbox, FormControlLabel, Link as MuiLink
} from '@mui/material';
import {
  Visibility, VisibilityOff, LockOutlined, EmailOutlined,
  BusinessOutlined, ArrowForward, CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import deLocale from 'date-fns/locale/de';
import { MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import KuhkopfLogo from '../assets/images/ALPINOR_LOGO_FINAL.png';
import { API_BASE_URL } from '../config/api';
import CookieBanner from '../components/CookieBanner';
// SystemLaunch removed

export default function Login() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = useState(window.location.pathname.includes('registration') ? 'register' : 'login');
  const [step, setStep] = useState(1);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: '', 
    firstName: '', 
    lastName: '', 
    confirmPassword: '',
    birthDate: null, 
    gender: '', 
    street: '', 
    houseNumber: '', 
    zipCode: '', 
    city: '',
    agbConsent: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInvited, setIsInvited] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleAuthFlow = async () => {
      const url = window.location.href;
      const hash = window.location.hash;
      
      const isTokenInUrl = url.includes('type=invite') || 
                           url.includes('type=recovery') || 
                           hash.includes('type=invite') || 
                           hash.includes('type=recovery') ||
                           hash.includes('access_token=');

      const { data: { session } } = await supabase.auth.getSession();
      
      if (isTokenInUrl || (session && localStorage.getItem('isAuthenticated') !== 'true')) {
        setMode('register');
        setIsInvited(true);
        if (session?.user) {
          setCredentials(prev => ({ ...prev, email: session.user.email || '' }));
          setSuccess('Willkommen im Team! Bitte vervollständige nun dein Profil.');
        }
      }

      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
        if (event === 'SIGNED_IN' && localStorage.getItem('isAuthenticated') !== 'true') {
          setMode('register');
          setIsInvited(true);
          if (newSession?.user) {
            setCredentials(prev => ({ ...prev, email: newSession.user.email || '' }));
          }
        }
      });

      return () => subscription.unsubscribe();
    };

    handleAuthFlow();
  }, []);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const getPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (/[A-Z]/.test(pwd)) strength += 25;
    if (/[0-9]/.test(pwd)) strength += 25;
    if (/[^A-Za-z0-9]/.test(pwd)) strength += 25;
    return strength;
  };

  const handleNext = () => {
    if (step === 1) {
      if (!credentials.firstName || !credentials.lastName || !credentials.birthDate || !credentials.gender || !credentials.street || !credentials.houseNumber || !credentials.zipCode || !credentials.city) {
        setError('Bitte füllen Sie alle Pflichtfelder aus.');
        return;
      }
      setError('');
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && step === 1) {
      handleNext();
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (mode === 'login') {
        const { data, error: authError } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });
        if (authError) throw authError;
        if (data.user) {
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('user', JSON.stringify(data.user));
          sessionStorage.setItem('launchShown', 'true'); 
          navigate('/');
        }
      } else {
        if (credentials.password !== credentials.confirmPassword) {
          throw new Error('Passwörter stimmen nicht überein.');
        }
        if (!credentials.agbConsent) {
          throw new Error('Sie müssen den AGB und den Datenschutzbestimmungen zustimmen.');
        }
        if (getPasswordStrength(credentials.password) < 100) {
          throw new Error('Passwort ist nicht sicher genug.');
        }
        
        const fullName = `${credentials.firstName} ${credentials.lastName}`.trim();
        const fullStreet = `${credentials.street} ${credentials.houseNumber}`.trim();

        if (isInvited) {
          const { data, error: updateError } = await supabase.auth.updateUser({
            password: credentials.password,
            data: {
              full_name: fullName,
              first_name: credentials.firstName,
              last_name: credentials.lastName
            }
          });
          if (updateError) throw updateError;

          try {
            await fetch(`${API_BASE_URL}/api/employees/sync-onboarding`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: credentials.email,
                firstName: credentials.firstName,
                lastName: credentials.lastName,
                birthDate: credentials.birthDate,
                gender: credentials.gender,
                street: fullStreet,
                zipCode: credentials.zipCode,
                city: credentials.city,
                status: 'aktiv',
                agbAccepted: true
              })
            });
          } catch (syncErr) {
            console.error('Onboarding Sync failed:', syncErr);
          }

          setSuccess('Profil erfolgreich vervollständigt! Willkommen an Bord.');
          setTimeout(() => {
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('user', JSON.stringify(data.user));
            navigate('/');
          }, 2000);
        } else {
          const { data, error: regError } = await supabase.auth.signUp({
            email: credentials.email,
            password: credentials.password,
            options: {
              data: {
                full_name: fullName,
                first_name: credentials.firstName,
                last_name: credentials.lastName
              }
            }
          });
          if (regError) throw regError;

          try {
            await fetch(`${API_BASE_URL}/api/employees/sync-onboarding`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: credentials.email,
                firstName: credentials.firstName,
                lastName: credentials.lastName,
                birthDate: credentials.birthDate,
                gender: credentials.gender,
                street: fullStreet,
                zipCode: credentials.zipCode,
                city: credentials.city,
                status: 'aktiv',
                agbAccepted: true
              })
            });
          } catch (syncErr) {
            console.error('Onboarding Sync failed:', syncErr);
          }

          setSuccess('Registrierung erfolgreich! Bitte prüfe deine E-Mails.');
        }
      }
    } catch (err) {
      setError(err.message || 'Aktion fehlgeschlagen. Bitte prüfen Sie Ihre Eingaben.');
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(credentials.password);
  const strengthColor = strength === 100 ? '#10b981' : strength >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <Box sx={{ 
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', 
      bgcolor: '#ffffff',
      backgroundImage: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #f8fafc 100%)',
      overflow: 'hidden', position: 'relative'
    }}>
      <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, bgcolor: 'rgba(59, 130, 246, 0.03)', borderRadius: '50%', filter: 'blur(100px)' }} />
      <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, bgcolor: 'rgba(139, 92, 246, 0.03)', borderRadius: '50%', filter: 'blur(100px)' }} />

      <Fade in timeout={1000}>
        <Box sx={{ width: '100%', maxWidth: mode === 'register' ? 700 : 450, px: 2, zIndex: 10 }}>
          <Paper elevation={0} sx={{
            p: { xs: 4, md: 6 }, borderRadius: 8, bgcolor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)', border: '1px solid #f1f5f9',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
            textAlign: 'center'
          }}>
            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ height: 80, mb: 2 }}>
                <img src={KuhkopfLogo} alt="Logo" style={{ height: '100%' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mb: 1, letterSpacing: -0.5 }}>
                {isInvited ? 'Account vervollständigen' : (mode === 'login' ? 'Willkommen' : 'Konto erstellen')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Seifenmanufaktur Allgäu Management</Typography>
              
              {mode === 'register' && (
                <Box sx={{ mt: 3, width: '100%', display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Box sx={{ height: 6, width: 40, borderRadius: 3, bgcolor: step >= 1 ? '#3b82f6' : '#e2e8f0', transition: 'all 0.3s' }} />
                  <Box sx={{ height: 6, width: 40, borderRadius: 3, bgcolor: step >= 2 ? '#3b82f6' : '#e2e8f0', transition: 'all 0.3s' }} />
                </Box>
              )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3, bgcolor: '#fef2f2', color: '#991b1b', border: '1px solid #fee2e2' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 3, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #dcfce7' }}>{success}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                {mode === 'register' && step === 1 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Vorname" name="firstName" required
                        variant="outlined" value={credentials.firstName} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Nachname" name="lastName" required
                        variant="outlined" value={credentials.lastName} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth select label="Geschlecht" name="gender" required
                        variant="outlined" value={credentials.gender} onChange={handleChange}
                        sx={inputStyle}
                      >
                        <MenuItem value="männlich">Männlich</MenuItem>
                        <MenuItem value="weiblich">Weiblich</MenuItem>
                        <MenuItem value="divers">Divers</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
                        <DatePicker
                          label="Geburtsdatum"
                          open={isPickerOpen}
                          onOpen={() => setIsPickerOpen(true)}
                          onClose={() => setIsPickerOpen(false)}
                          value={credentials.birthDate ? new Date(credentials.birthDate) : null}
                          onChange={(date) => setCredentials({ ...credentials, birthDate: date ? date.toISOString().split('T')[0] : '' })}
                          slots={{
                            openPickerButton: () => null,
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              sx: inputStyle,
                              onClick: () => setIsPickerOpen(true),
                              InputProps: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IconButton 
                                      size="small" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPickerOpen(true);
                                      }}
                                      sx={{ color: '#94a3b8', ml: -0.5 }}
                                    >
                                      <CalendarIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={8} sm={8}>
                      <TextField
                        fullWidth label="Straße" name="street" required
                        variant="outlined" value={credentials.street} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={4} sm={4}>
                      <TextField
                        fullWidth label="Hausnr." name="houseNumber" required
                        variant="outlined" value={credentials.houseNumber} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth label="PLZ" name="zipCode" required
                        variant="outlined" value={credentials.zipCode} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth label="Ort" name="city" required
                        variant="outlined" value={credentials.city} onChange={handleChange}
                        sx={inputStyle}
                      />
                    </Grid>
                  </>
                )}

                {(mode === 'login' || (mode === 'register' && step === 2)) && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth label="E-Mail" name="email" required
                        disabled={isInvited}
                        variant="outlined" value={credentials.email} onChange={handleChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: '#94a3b8' }} /></InputAdornment>,
                        }}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <TextField
                          fullWidth label="Passwort" name="password" required
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined" value={credentials.password} onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: '#94a3b8' }} /></InputAdornment>,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#94a3b8' }}>
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={inputStyle}
                        />
                        {mode === 'register' && credentials.password && (
                          <Box sx={{ mt: 1, px: 1 }}>
                            <Box sx={{ height: 4, width: '100%', bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${strength}%`, bgcolor: strengthColor, transition: 'all 0.3s' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 700, mt: 0.5, display: 'block', textAlign: 'left' }}>
                              {strength === 100 ? 'Sicheres Passwort' : strength >= 50 ? 'Mittleres Passwort' : 'Unsicheres Passwort'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {mode === 'register' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="Passwort wiederholen" name="confirmPassword" required
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined" value={credentials.confirmPassword} onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: '#94a3b8' }} /></InputAdornment>,
                          }}
                          sx={inputStyle}
                        />
                      </Grid>
                    )}

                    {mode === 'register' && step === 2 && (
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <FormControlLabel
                          sx={{ textAlign: 'center', alignItems: 'center', m: 0 }}
                          control={
                            <Checkbox 
                              name="agbConsent" 
                              checked={credentials.agbConsent} 
                              onChange={(e) => setCredentials({ ...credentials, agbConsent: e.target.checked })} 
                              color="primary" 
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              Ich stimme den <MuiLink href="#" underline="hover" sx={{ fontWeight: 600, color: '#3b82f6' }}>AGB</MuiLink>, den <MuiLink href="#" underline="hover" sx={{ fontWeight: 600, color: '#3b82f6' }}>Nutzungsbedingungen</MuiLink> und der <MuiLink href="#" underline="hover" sx={{ fontWeight: 600, color: '#3b82f6' }}>Datenschutzerklärung</MuiLink> zu.*
                            </Typography>
                          }
                        />
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    {mode === 'register' && step === 2 && (
                      <Button 
                        onClick={handleBack} fullWidth variant="outlined"
                        sx={{
                          py: 2, borderRadius: 4, borderColor: '#e2e8f0', color: '#64748b', 
                          fontWeight: 900, fontSize: '1rem', textTransform: 'none',
                          '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                        }}
                      >
                        Zurück
                      </Button>
                    )}
                    <Button 
                      type="submit" fullWidth variant="contained" 
                      disabled={loading}
                      endIcon={!loading && <ArrowForward />}
                      sx={{
                        py: 2, borderRadius: 4, bgcolor: '#3b82f6', color: '#fff', 
                        fontWeight: 900, fontSize: '1rem', textTransform: 'none',
                        boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)',
                        '&:hover': { bgcolor: '#2563eb', boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.4)' },
                        '&.Mui-disabled': { bgcolor: 'rgba(59, 130, 246, 0.3)', color: 'rgba(255,255,255,0.5)' }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 
                        (mode === 'register' && step === 1 ? 'Weiter' : 
                        (isInvited ? 'Account aktivieren' : (mode === 'login' ? 'Anmelden' : 'Registrieren')))}
                    </Button>
                  </Stack>
                </Grid>

                {!isInvited && (
                  <Grid item xs={12}>
                    <Button
                      onClick={() => {
                        setMode(mode === 'login' ? 'register' : 'login');
                        setStep(1);
                      }}
                      sx={{ textTransform: 'none', fontWeight: 700, color: '#3b82f6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                    >
                      {mode === 'login' ? 'Noch kein Konto? Registrieren' : 'Bereits ein Konto? Anmelden'}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: 1.5 }}>
                SECURE ACCESS • V3.0
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>
      <CookieBanner />
    </Box>
  );
};

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    color: '#1e293b',
    '& fieldset': { 
      borderColor: '#e2e8f0', 
      borderRadius: 4,
      transition: 'all 0.2s ease-in-out'
    },
    '&:hover fieldset': { 
      borderColor: '#cbd5e1',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
    },
    '&.Mui-focused fieldset': { 
      borderColor: '#3b82f6',
      borderWidth: '2px'
    },
    bgcolor: '#f8fafc',
    '& input': {
      py: 2,
      '&:-webkit-autofill': {
        '-webkit-box-shadow': '0 0 0 100px #f8fafc inset !important',
        '-webkit-text-fill-color': '#1e293b !important',
      },
    },
    '& .MuiSelect-select': {
      py: 2,
      textAlign: 'left'
    }
  },
  '& .MuiInputLabel-root': { 
    color: '#64748b', 
    fontWeight: 600,
    transform: 'translate(14px, 16px) scale(1)'
  },
  '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-shrink': { 
    color: '#3b82f6',
    transform: 'translate(14px, -9px) scale(0.75)'
  },
};

